import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { MouseEvent, useContext } from 'react'
import { mutate } from 'swr'
import { match } from 'ts-pattern'
import { Button } from 'components/common/Button'
import { useGetUserEventFavorites } from 'containers/home/useGetUserEventFavorites'
import { apiClient } from 'services/api/client'
import { AuthModalContext } from './Auth/AuthModalContext'
import { HeartIcon } from './EventCard.style'
import { UserActionsContext } from './User/UserActionsContext'
import { NewFlexContainer } from './common/common.style'

interface ButtonHeartProps {
  eventId: number
  small?: boolean
}

interface HeartIconContainerProps {
  selected?: boolean
  outlineHeart?: boolean
}

const HeartIconContainer = ({
  selected,
  outlineHeart
}: HeartIconContainerProps) => {
  return (
    <NewFlexContainer>
      <HeartIcon $selected={selected} $outlineHeart={outlineHeart} />
    </NewFlexContainer>
  )
}

export const ButtonHeart = ({ eventId, small }: ButtonHeartProps) => {
  const { t } = useTranslation('common')
  const session = useSession()
  const { setEventId } = useContext(UserActionsContext)
  const { setModalParams } = useContext(AuthModalContext)
  const router = useRouter()

  const { data: userEventFavorites } = useGetUserEventFavorites()
  const isEventSelected = userEventFavorites?.data.some(
    (favorite) => favorite.eventId === eventId
  )
  const toggleFavorite = async () => {
    await (isEventSelected ? apiClient.delete : apiClient.post)(
      `/user-event-favorite/${eventId}`
    )
    mutate('user-event-favorites')
  }

  const displaySignInModal = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setEventId(eventId)
    setModalParams({
      redirectUrl: router.asPath,
      state: 'SIGNIN'
    })
  }

  return match(session)
    .with({ status: 'loading' }, () => null)
    .with({ status: 'authenticated' }, () =>
      small ? (
        <Button
          icon={<HeartIconContainer selected={isEventSelected} outlineHeart />}
          onClick={toggleFavorite}
        />
      ) : (
        <Button
          label={
            isEventSelected
              ? t('eventPage.savedFavorite')
              : t('eventPage.saveFavorite')
          }
          leftIcon={
            <HeartIconContainer selected={isEventSelected} outlineHeart />
          }
          onClick={toggleFavorite}
        />
      )
    )
    .with({ status: 'unauthenticated' }, () =>
      small ? (
        <Button
          icon={<HeartIconContainer selected={false} outlineHeart />}
          onClick={displaySignInModal}
        />
      ) : (
        <Button
          label={t('eventPage.saveFavorite')}
          leftIcon={<HeartIconContainer selected={false} outlineHeart />}
          onClick={displaySignInModal}
        />
      )
    )
    .exhaustive()
}
